import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Cylinder as CylinderIcon,
  ImageOff,
  Printer,
} from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import CylinderFormDialog from "../components/cylinder/CylinderFormDialog";
import ViewCylinderDialog from "../components/cylinder/ViewCylinderDialog";

const MASTER_KEYS = {
  retail_outlets: "retail_outlet_id",
  wheel_counts: "wheel_count_id",
  vehicle_brands: "vehicle_brand_id",
  engine_stroke_types: "engine_stroke_type_id",
  cylinder_categories: "cylinder_category_id",
};

function LabelPrintCard({ record }) {
  const canvasRef = useRef(null);

  const handlePrint = () => {
    if (!record) return;
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error("QR code is not ready yet, please retry in a moment.");
      return;
    }
    try {
      const dataUrl = canvas.toDataURL("image/png");
      // Page size: 75mm x 100mm (matches reference)
      const doc = new jsPDF({ unit: "mm", format: [75, 100], orientation: "portrait" });
      const topText = (record.vehicle_number || "").trim() || record.temp_number;
      const bottomText = record.temp_number || "";

      doc.setFont("helvetica", "bold");

      // Top text (vehicle number) — larger
      doc.setFontSize(22);
      doc.text(topText, 37.5, 14, { align: "center" });

      // QR code in the centre
      const qrSize = 52; // mm
      doc.addImage(dataUrl, "PNG", (75 - qrSize) / 2, 22, qrSize, qrSize);

      // Bottom text (temp number) — smaller
      doc.setFontSize(16);
      doc.text(bottomText, 37.5, 88, { align: "center" });

      const filename = `${record.temp_number || "fuel-access"}.pdf`;
      doc.save(filename);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    }
  };

  const topText = (record?.vehicle_number || "").trim() || record?.temp_number || "";
  const bottomText = record?.temp_number || "";

  return (
    <div
      className="bg-white border border-black/5 rounded-xl p-6"
      data-testid="label-print-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-[#0A0A0A]">Print Label</h2>
          <p className="text-xs text-[#525252] mt-0.5">
            75 × 100 mm PDF label with the fuel access QR code.
          </p>
        </div>
        <Button
          onClick={handlePrint}
          className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-9"
          data-testid="print-label-button"
        >
          <Printer className="h-4 w-4 mr-1.5" />
          Print
        </Button>
      </div>

      <div className="flex items-center justify-center">
        <div className="bg-white border border-black/10 rounded-md px-6 py-5 flex flex-col items-center min-w-[240px]">
          <p className="text-lg font-bold tracking-wide text-[#0A0A0A] uppercase">
            {topText || <span className="text-[#A3A3A3]">—</span>}
          </p>
          <div className="my-3">
            <QRCodeCanvas
              ref={canvasRef}
              value={record?.id || ""}
              size={180}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#000000"
            />
          </div>
          <p className="text-sm font-bold tracking-wide text-[#0A0A0A] uppercase">
            {bottomText || <span className="text-[#A3A3A3]">—</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

const NotFilled = () => (
  <span className="text-[#A3A3A3] italic">Not Filled</span>
);

// Cylinder test-due status: red (overdue), yellow (due within 30 days), green (ok)
function dueTestStatus(dueIso) {
  if (!dueIso) return { tone: "gray", days: null };
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return { tone: "gray", days: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { tone: "red", days: diffDays };
  if (diffDays <= 30) return { tone: "yellow", days: diffDays };
  return { tone: "green", days: diffDays };
}

function StatusDot({ tone }) {
  const styles = {
    red: "bg-[#F50E02] ring-[#F50E02]/20",
    yellow: "bg-amber-500 ring-amber-500/20",
    green: "bg-emerald-500 ring-emerald-500/20",
    gray: "bg-[#A3A3A3] ring-[#A3A3A3]/20",
  };
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full ring-4 ${styles[tone]}`}
      aria-label={tone}
    />
  );
}

function dueInLabel(days) {
  if (days === null) return "—";
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function formatDate(iso) {
  if (!iso) return "";
  if (!/\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function InfoRow({ label, value }) {
  const empty =
    value == null || (typeof value === "string" && !value.trim());
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-black/5">
      <span className="text-xs font-semibold uppercase tracking-wider text-[#525252] shrink-0 w-1/2">
        {label}
      </span>
      <span className="text-sm text-[#0A0A0A] text-right break-words w-1/2">
        {empty ? <NotFilled /> : value}
      </span>
    </div>
  );
}

function ImageRow({ label, src }) {
  return (
    <div className="py-3 border-b border-black/5 last:border-0">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2">
        {label}
      </p>
      {src ? (
        <img
          src={src}
          alt={label}
          className="max-h-40 rounded-md border border-black/10 bg-[#FAFAFA] object-contain"
        />
      ) : (
        <div className="inline-flex items-center gap-2 text-sm text-[#A3A3A3]">
          <ImageOff className="h-4 w-4" />
          <span className="italic">Not Filled</span>
        </div>
      )}
    </div>
  );
}

export default function FuelAccessView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  const [masterMaps, setMasterMaps] = useState({});
  const [capacityMap, setCapacityMap] = useState({});

  const [cylinders, setCylinders] = useState([]);
  const [cylLoading, setCylLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    setCylLoading(true);
    try {
      const masterKeys = Object.keys(MASTER_KEYS);
      const [faRes, cylRes, capRes, ...mRes] = await Promise.all([
        api.get(`/fuel-access/${id}`),
        api.get(`/fuel-access/${id}/cylinders`),
        api.get("/master/cylinder_capacities"),
        ...masterKeys.map((k) => api.get(`/master/${k}`)),
      ]);
      setRecord(faRes.data);
      setCylinders(cylRes.data || []);
      const cap = {};
      for (const c of capRes.data || []) cap[c.id] = c.name;
      setCapacityMap(cap);
      const maps = {};
      masterKeys.forEach((k, idx) => {
        const m = {};
        for (const it of mRes[idx].data || []) m[it.id] = it.name;
        maps[k] = m;
      });
      setMasterMaps(maps);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load fuel access"
      );
      navigate("/fuel-access", { replace: true });
    } finally {
      setLoading(false);
      setCylLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const reloadCylinders = async () => {
    setCylLoading(true);
    try {
      const { data } = await api.get(`/fuel-access/${id}/cylinders`);
      setCylinders(data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to refresh cylinders"
      );
    } finally {
      setCylLoading(false);
    }
  };

  const handleDeleteCylinder = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/cylinders/${deleteTarget.id}`);
      setCylinders((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast.success("Cylinder deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"
      );
    } finally {
      setDeleting(false);
    }
  };

  const resolve = (mapKey, idValue) => {
    if (!idValue) return "";
    return masterMaps[mapKey]?.[idValue] || "";
  };

  const fa = record || {};
  const statusBlank = fa.status === "blank";

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#F50E02]" />
      </div>
    );
  }

  return (
    <div data-testid="fuel-access-view-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/fuel-access")}
          className="h-9 w-9 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
          aria-label="Back"
          data-testid="view-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            Fuel Access <span className="text-[#F50E02]">— {fa.temp_number}</span>
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Status:{" "}
            <span
              className={`font-semibold uppercase tracking-wider ${
                statusBlank ? "text-[#F50E02]" : "text-emerald-700"
              }`}
            >
              {statusBlank ? "Fuel Excess" : fa.status}
            </span>
          </p>
        </div>
        {statusBlank ? (
          <Button
            variant="outline"
            onClick={() => navigate(`/fuel-access/${id}/details`)}
            className="border-black/10"
            data-testid="view-add-details"
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Add Details
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => navigate(`/fuel-access/${id}/details`)}
            className="border-black/10"
            data-testid="view-edit-details"
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit Details
          </Button>
        )}
      </header>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Label print + Fuel Access info */}
        <div className="space-y-6">
          <LabelPrintCard record={record} />

          <div className="bg-white border border-black/5 rounded-xl p-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-[#0A0A0A]">
              Fuel Access Details
            </h2>
            <p className="text-xs text-[#525252] mt-0.5">
              Empty fields show as <b>Not Filled</b>.
            </p>
          </div>
          <InfoRow label="Temp Number" value={fa.temp_number} />
          <InfoRow label="QR Code" value={fa.qr_code} />
          <InfoRow label="Retail Outlet" value={resolve("retail_outlets", fa.retail_outlet_id)} />
          <InfoRow label="Prefix" value={fa.prefix_name} />
          <InfoRow label="Wheeler" value={resolve("wheel_counts", fa.wheel_count_id)} />
          <InfoRow label="Vehicle Brand" value={resolve("vehicle_brands", fa.vehicle_brand_id)} />
          <InfoRow label="Engine Stroke Type" value={resolve("engine_stroke_types", fa.engine_stroke_type_id)} />
          <InfoRow label="Category of Cylinder" value={resolve("cylinder_categories", fa.cylinder_category_id)} />
          <InfoRow label="Vehicle Number" value={fa.vehicle_number} />
          <InfoRow label="Kit Installation By" value={fa.kit_installation_by} />
          <InfoRow label="Kit Installed Date" value={formatDate(fa.kit_installed_date)} />
          <InfoRow label="Kit Serial Number" value={fa.kit_serial_number} />
          <InfoRow label="Kit Manufacturer Name" value={fa.kit_manufacturer_name} />
          <InfoRow label="Engine Number" value={fa.engine_number} />
          <InfoRow label="Chassis Number" value={fa.chassis_number} />
          <InfoRow label="Vehicle Manufacturer Date" value={formatDate(fa.vehicle_manufacturer_date)} />
          <InfoRow label="Owner Name" value={fa.owner_name} />
          <InfoRow label="Owner Phone Number" value={fa.owner_phone_number} />
          <InfoRow label="Permit Number" value={fa.permit_number} />
          <InfoRow label="Owner Address" value={fa.owner_address} />
          <ImageRow label="RC Picture" src={fa.rc_picture} />
          <ImageRow label="Vehicle Picture" src={fa.vehicle_picture} />
          <InfoRow
            label="Created By"
            value={
              fa.created_by_name || fa.created_by_email
                ? `${fa.created_by_name || "—"}${fa.created_by_email ? ` (${fa.created_by_email})` : ""}`
                : ""
            }
          />
          <InfoRow
            label="Registered By"
            value={
              fa.registered_by_name || fa.registered_by_email
                ? `${fa.registered_by_name || "—"}${fa.registered_by_email ? ` (${fa.registered_by_email})` : ""}`
                : ""
            }
          />
          </div>
        </div>

        {/* Right: Cylinders */}
        <div className="bg-white border border-black/5 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-[#0A0A0A]">Cylinders</h2>
              <p className="text-xs text-[#525252] mt-0.5">
                {cylinders.length} {cylinders.length === 1 ? "cylinder" : "cylinders"} attached to this fuel access.
              </p>
            </div>
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-9"
              data-testid="add-cylinder-button"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Cylinder
            </Button>
          </div>

          <div className="border border-black/5 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cylinders-table">
                <thead>
                  <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Sr.No.</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">QR Code</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">QR Code / Fuel Access Sticker Value</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Testing Due In / Status</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cylLoading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-[#525252]">
                        <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                      </td>
                    </tr>
                  ) : cylinders.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-[#525252]" data-testid="cylinders-empty">
                        <div className="inline-flex flex-col items-center gap-2">
                          <CylinderIcon className="h-6 w-6 text-[#A3A3A3]" />
                          <span>No cylinders yet. Click &quot;Add Cylinder&quot;.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    cylinders.map((c, idx) => {
                      const { tone, days } = dueTestStatus(c.due_test_date);
                      return (
                        <tr
                          key={c.id}
                          className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60 align-top"
                          data-testid={`cylinder-row-${c.id}`}
                        >
                          <td className="px-4 py-4 text-[#525252]">{idx + 1}</td>
                          <td className="px-4 py-4">
                            <div
                              className="p-1.5 bg-white rounded-md border border-black/5 inline-block"
                              title={c.id}
                            >
                              <QRCodeSVG
                                value={c.id}
                                size={72}
                                level="M"
                                includeMargin={false}
                                bgColor="#FFFFFF"
                                fgColor="#0A0A0A"
                              />
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-2">
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
                                  QR Code
                                </div>
                                <div className="text-sm font-medium text-[#0A0A0A]">
                                  {c.qr_code || "—"}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
                                  Fuel Access Sticker Value
                                </div>
                                <div className="text-sm text-[#0A0A0A]">
                                  {c.fuel_access_sticker_value || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-2">
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
                                  Testing Due In
                                </div>
                                <div className="text-sm text-[#0A0A0A]">
                                  {dueInLabel(days)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
                                  Status
                                </div>
                                <div className="mt-1">
                                  <StatusDot tone={tone} />
                                </div>
                              </div>
                            </div>
                          </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setViewTarget(c)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="View"
                              title="View"
                              data-testid={`cylinder-view-${c.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditTarget(c)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Edit"
                              title="Edit"
                              data-testid={`cylinder-edit-${c.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(c)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Delete"
                              title="Delete"
                              data-testid={`cylinder-delete-${c.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add cylinder */}
      <CylinderFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        fuelAccessId={id}
        fuelAccessPrefixId={fa?.prefix_id || ""}
        fuelAccessPrefixName={fa?.prefix_name || ""}
        cylinder={null}
        onSaved={() => reloadCylinders()}
      />

      {/* Edit cylinder */}
      <CylinderFormDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        fuelAccessId={id}
        fuelAccessPrefixId={fa?.prefix_id || ""}
        fuelAccessPrefixName={fa?.prefix_name || ""}
        cylinder={editTarget}
        onSaved={(updated) => {
          setCylinders((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c))
          );
          setEditTarget(null);
        }}
      />

      {/* View cylinder */}
      <ViewCylinderDialog
        open={!!viewTarget}
        onOpenChange={(o) => !o && setViewTarget(null)}
        cylinder={viewTarget}
        capacityMap={capacityMap}
      />

      {/* Delete cylinder */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete cylinder?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <b>{deleteTarget?.fuel_access_sticker_value}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cylinder-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteCylinder();
              }}
              disabled={deleting}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="cylinder-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
